import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '@/shared/components/Navigation';
import ToastContainer from '@/shared/components/ToastContainer';
import { useAuthContext } from '@/modules/auth/ui/AuthProvider';
import { useToast } from '@/hooks/useToast';
import { SendQuoteModal, DeclineQuoteModal } from '@/modules/admin/components/AdminModals';
import { ADMIN_EMAILS } from '../../../../shared/config/admins.js';
import { useAdminApp } from '@/hooks/queries';
import { captureError } from '@/utils/errorHandler';

export default function AdminCreateQuotePage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { user, authFetch } = useAuthContext();
  const { showToast, toasts, hideToast } = useToast();

  // Query hooks
  const { data: app, isLoading: appLoading, error: appError } = useAdminApp(appId);
  
  const loading = appLoading;

  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteCurrency, setQuoteCurrency] = useState('usd');
  const [monthlyPrice, setMonthlyPrice] = useState('5.00');
  const [quoteNote, setQuoteNote] = useState('');
  const [depositPercentage, setDepositPercentage] = useState(20);
  const [declineNote, setDeclineNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [declining, setDeclining] = useState(false);

  useEffect(() => {
    // Check if user is admin
    if (!ADMIN_EMAILS.includes(user?.email)) {
      showToast('Access denied. Admin only.', 'error');
      navigate('/admin-portal');
    }
  }, [user?.email]);

  useEffect(() => {
    if (appError) {
      showToast('Failed to load app data', 'error');
      navigate('/admin-portal/quotes-needed');
    }
  }, [appError]);

  // Set currency from app when it loads
  useEffect(() => {
    if (app) {
      setQuoteCurrency(app.preferredCurrency || 'usd');
    }
  }, [app]);

  const handleSendQuote = () => {
    setQuoteAmount(app?.quotedAmount || '');
    setQuoteCurrency(app?.preferredCurrency || 'usd');
    setMonthlyPrice(app?.monthlyMaintenanceFee || '5.00');
    setDepositPercentage(20);
    setShowQuoteModal(true);
  };

  const submitQuote = async () => {
    if (!quoteAmount) {
      showToast('Please enter the total amount', 'error');
      return;
    }

    if (!monthlyPrice) {
      showToast('Please enter the monthly price', 'error');
      return;
    }

    setSubmitting(true);
    try {
      // Backend handles currency logic: if user has preferredCurrency, it uses that (locked).
      // If user doesn't have preferredCurrency, it uses the currency from the request.
      // Always send quoteCurrency - admin's selection when user has no preference.
      const currency = quoteCurrency || app?.preferredCurrency || 'usd';
      
      const response = await authFetch('/api/admin/send-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId,
          totalAmount: parseFloat(quoteAmount),
          currency,
          monthlyPrice: parseFloat(monthlyPrice),
          note: quoteNote.trim() || null,
          depositPercentage: Number(depositPercentage),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send quote');
      }

      showToast('✅ Quote sent successfully!', 'success');
      setShowQuoteModal(false);
      
      // Redirect back to quotes-needed page after a short delay
      setTimeout(() => {
        navigate('/admin-portal/quotes-needed');
      }, 1500);
    } catch (error) {
      captureError(error, {
        action: 'submitQuote',
        component: 'AdminCreateQuotePage',
        extra: { appId, quoteAmount, quoteCurrency, monthlyPrice },
      });
      console.error('Error sending quote:', error);
      showToast(error.message || 'Failed to send quote', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const closeQuoteModal = () => {
    setShowQuoteModal(false);
    setQuoteAmount('');
    setMonthlyPrice('5.00');
    setQuoteNote('');
  };

  const handleDeclineQuote = () => {
    setDeclineNote('');
    setShowDeclineModal(true);
  };

  const closeDeclineModal = () => {
    setShowDeclineModal(false);
    setDeclineNote('');
  };

  const submitDeclineQuote = async () => {
    setDeclining(true);
    try {
      const response = await authFetch('/api/admin/decline-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId,
          note: declineNote.trim() || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to decline quote');
      }

      showToast('✅ Quote declined successfully', 'success');
      setShowDeclineModal(false);
      setDeclineNote('');
      
      // Redirect back to quotes-needed page after a short delay
      setTimeout(() => {
        navigate('/admin-portal/quotes-needed');
      }, 1500);
    } catch (error) {
      captureError(error, {
        action: 'submitDeclineQuote',
        component: 'AdminCreateQuotePage',
        extra: { appId, declineNote },
      });
      console.error('Error declining quote:', error);
      showToast(error.message || 'Failed to decline quote', 'error');
    } finally {
      setDeclining(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-zapt-purple mb-4"></div>
            <p className="text-gray-600">Loading app details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!app) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <ToastContainer toasts={toasts} onCloseToast={hideToast} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/admin-portal/quotes-needed')}
          className="inline-flex items-center text-zapt-purple hover:text-zapt-purple/80 font-medium mb-6"
        >
          <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Quotes Needed
        </button>

        {/* App Header */}
        <div className="bg-white rounded-lg border-2 border-blue-200 p-6 mb-6 shadow-sm">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {app.name || 'Untitled App'}
              </h1>
              <p className="text-sm text-gray-500 mb-2">
                User: {app.userEmail || 'Unknown'}
              </p>
              {app.description && (
                <p className="text-gray-600 mb-3">
                  {app.description}
                </p>
              )}
              {app.preferredCurrency && (
                <p className="text-xs text-gray-500">
                  Preferred Currency: {app.preferredCurrency.toUpperCase()}
                </p>
              )}
            </div>
            <div className="ml-4">
              <button
                onClick={() => navigate(`/admin-portal/app/${appId}`)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
              >
                View Full App →
              </button>
            </div>
          </div>
        </div>

        {/* App Details Section */}
        {app.details && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Features & Details
            </h2>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans">{app.details}</pre>
            </div>
          </div>
        )}

        {/* Send Quote Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Send Quote
          </h2>
          <p className="text-gray-600 mb-6">
            Review the app details above and create a quote for the user. Once sent, they will receive an email with the quote details.
          </p>
          <div className="flex gap-3">
            <button
              onClick={handleSendQuote}
              className="px-6 py-3 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 transition-colors font-medium"
            >
              📋 Create & Send Quote
            </button>
            <button
              onClick={handleDeclineQuote}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              ❌ Decline Quote
            </button>
          </div>
        </div>
      </div>

      {/* Send Quote Modal */}
      <SendQuoteModal
        show={showQuoteModal}
        app={app}
        activeRequest={app?.quotedAt ? { id: appId } : null}
        quoteAmount={quoteAmount}
        quoteCurrency={quoteCurrency}
        monthlyPrice={monthlyPrice}
        quoteNote={quoteNote}
        depositPercentage={depositPercentage}
        onDepositPercentageChange={setDepositPercentage}
        submitting={submitting}
        onClose={closeQuoteModal}
        onSubmit={submitQuote}
        onAmountChange={setQuoteAmount}
        onCurrencyChange={setQuoteCurrency}
        onMonthlyPriceChange={setMonthlyPrice}
        onNoteChange={setQuoteNote}
      />

      {/* Decline Quote Modal */}
      <DeclineQuoteModal
        show={showDeclineModal}
        app={app}
        note={declineNote}
        submitting={declining}
        onClose={closeDeclineModal}
        onSubmit={submitDeclineQuote}
        onNoteChange={setDeclineNote}
      />
    </div>
  );
}
